import { Type } from '@angular/core';
import { Route, Routes } from '@angular/router';

import { DefaultLayoutComponent, maintenanceGuard, noMaintenanceGuard, TosPageComponent } from 'app';
import { authGuard, communityAccessGuard, emailVerifiedGuard, legalAcknowledgementGuard } from 'auth';
import {
  CommunityListPageComponent,
  CommunityThreadDetailPageComponent,
  CommunityMemberPageComponent,
  CommunityModerationPageComponent,
  CommunityMessagesPageComponent,
  CommunityMessageComposePageComponent,
  CommunityMessageDetailPageComponent,
  CommunitySearchPageComponent,
  CommunityNotificationsPageComponent,
  CommunitySettingsPageComponent
} from 'community';
import { LegalAcknowledgementPageComponent } from '../containers/pages/legal-acknowledgement-page/legal-acknowledgement-page.component';
import { CommunityAdminPageComponent } from '../containers/pages/community-admin-page/community-admin-page.component';
import { LanguageGuard } from 'i18n';
import { MaintenancePageComponent } from '../containers/pages/maintenance-page/maintenance-page.component';
import { ConfirmEmailPageComponent } from '../containers/pages/confirm-email-page/confirm-email-page.component';
import { ConfirmEmailSetupPageComponent } from '../containers/pages/confirm-email-setup-page/confirm-email-setup-page.component';
import { ForgotPasswordPageComponent } from '../containers/pages/forgot-password-page/forgot-password-page.component';
import { LoginPageComponent, LOGIN_CONFIG, LoginActionLink, LoginConfig, MediaPageComponent } from 'app';
import { CrossLoginHandoffPageComponent } from '../containers/pages/cross-login-handoff-page/cross-login-handoff-page.component';
import { MainPageComponent } from '../containers/pages/main-page/main-page.component';
import { OrderDetailPageComponent } from '../containers/pages/order-detail-page/order-detail-page.component';
import { OrderWizardComponent } from '../containers/pages/order-wizard/order-wizard.component';
import { ProfilePageComponent } from '../containers/pages/profile-page/profile-page.component';
import { RegisterPageComponent } from '../containers/pages/register-page/register-page.component';
import { ResetPasswordPageComponent } from '../containers/pages/reset-password-page/reset-password-page.component';
import { UserOrdersPageComponent } from '../containers/pages/user-orders-page/user-orders-page.component';

import { mediaMatcher, infoMatcher, policyMatcher } from './matcher';

export class RouteBuilder {

  private mainComponent: any = MainPageComponent;

  private replacements = new Map<string, Route | Route[]>();
  private removed = new Set<string>();

  /** When set, users with unconfirmed email may only open these first path segments (e.g. dashboard, profile). */
  private emailUnverifiedAllowlist: string[] = [];

  /** Opt-in maintenance page support. When enabled, the backend feature flag `maintenance_mode` redirects all routes to `/:lang/maintenance`. */
  private maintenanceEnabled = false;
  private maintenanceComponent: Type<unknown> | null = null;

  /** Opt-in legal acknowledgement gate. See withLegalAcknowledgement(). */
  private legalAcknowledgementEnabled = false;

  /** Opt-in Community Network routes. See withCommunity(). */
  private communityEnabled = false;

  /** Opt-in override for the login page's policy links. See withLoginActions(). */
  private loginActions: LoginActionLink[] | null = null;
  /** Opt-in override for the login page's "Register" target. See withRegisterUrl(). */
  private registerUrl: string | null = null;

  private config = {
    root: [] as Routes,
    publicLang: [] as Routes,
    authenticated: [] as Routes,
    protected: [] as Routes,
  };

  main(component: any): this {
    this.mainComponent = component;
    return this;
  }

  root(...routes: Routes): this {
    this.config.root.push(...routes);
    return this;
  }

  lang = {
    public: (...routes: Routes) => {
      this.config.publicLang.push(...routes);
      return this;
    },
    // add more nested lang types if needed...
  };

  auth(...routes: Routes): this {
    this.config.authenticated.push(...routes);
    return this;
  }

  protected(...routes: Routes): this {
    this.config.protected.push(...routes);
    return this;
  }

  replace(path: string, route: Route | Route[]): this {
    this.replacements.set(path, route);
    this.removed.delete(path);
    return this;
  }

  replaceMany(routes: Record<string, Route | Route[]>): this {
    for (const path in routes) {
      this.replace(path, routes[path]);
    }
    return this;
  }

  remove(path: string): this {
    this.removed.add(path);
    this.replacements.delete(path);
    return this;
  }

  /**
   * Allow navigation under the main layout while email is unconfirmed, for these routes only
   * (first segment after language, e.g. "dashboard", "profile"). Others redirect to dashboard.
   */
  allowRoutesWhenEmailUnverified(segments: string[]): this {
    this.emailUnverifiedAllowlist = [...segments];
    return this;
  }

  /**
   * Override the policy links shown at the bottom of the login page.
   *
   * Without this the login page falls back to `terms_of_service` /
   * `privacy_policy` / `legal_notice` — the key names fac3less (config domain 0)
   * and mnlo (6) publish. Domains that name their documents differently
   * (distribution 4, globalhealth 5, wolfpack-assets 7 use `terms` / `privacy` /
   * `imprint`) must declare their own keys here, otherwise every link 500s with
   * ActionView::MissingTemplate: PolicyController renders the template of that
   * name out of the domain's media directory and there is no fallback.
   */
  /**
   * Point the login page's "Register" link somewhere other than `/register` —
   * a brand whose sign-up IS its acquisition funnel (Global Health: `/r`, the
   * root redirect into the lang-scoped membership funnel) must not offer the
   * plain account form next to it.
   */
  withRegisterUrl(url: string): this {
    this.registerUrl = url;
    return this;
  }

  withLoginActions(actions: LoginActionLink[]): this {
    this.loginActions = [...actions];
    return this;
  }

  /**
   * Enable maintenance mode handling for this app.
   * Adds a `/:lang/maintenance` route and guards all `:lang` children: when the backend feature flag
   * `maintenance_mode` is true, every navigation is redirected to the maintenance page.
   *
   * @param component Optional custom maintenance page component (defaults to the shared MaintenancePageComponent)
   */
  withMaintenance(component?: Type<unknown>): this {
    this.maintenanceEnabled = true;
    this.maintenanceComponent = component ?? MaintenancePageComponent;
    return this;
  }

  /**
   * Enable the mandatory legal acknowledgement gate (Legal & Compliance Center).
   *
   * Adds `/:lang/legal/acknowledge` and guards the main layout with
   * legalAcknowledgementGuard, so the dashboard stays locked until the customer has
   * acknowledged every document their branding domain requires.
   *
   * WHICH documents, and whether the gate engages at all, is decided by the backend
   * registry (data/config/legal_config.json) keyed by config domain — this call only
   * says "this app talks to a backend that implements the gate". A new frontend on
   * the same backend needs this line plus a registry entry; nothing else.
   *
   * Not enabled by default because apps must explicitly opt in to the legal API.
   */
  withLegalAcknowledgement(): this {
    this.legalAcknowledgementEnabled = true;
    return this;
  }

  /**
   * Enable the Community Network (one network across the WolfPack apps).
   *
   * Adds `community` (newest threads), `community/c/:channelId` (one channel),
   * `community/admin` (structure administration), `community/notifications`,
   * `community/settings` (notification preferences & push opt-in),
   * `community/search` and `community/:id` (thread) inside the main layout,
   * each behind communityAccessGuard. Which groups and channels a member sees is decided by
   * the backend (CommunityAccess); this call only says "this app talks to a
   * backend that implements the community API". Off by default.
   */
  withCommunity(): this {
    this.communityEnabled = true;
    return this;
  }

  private communityRoutes(): Routes {
    if (!this.communityEnabled) return [];
    // emailVerifiedGuard stays on the routes themselves (as the hand-copied
    // blocks had it): apps that allow-list routes while the e-mail is unverified
    // would otherwise let an unconfirmed user in, only to receive a 403 from
    // every community request.
    const guards = [emailVerifiedGuard, communityAccessGuard];
    // Order matters: every fixed two-segment path (`c/:channelId`, `admin`,
    // `moderation`) must be matched before the catch-all `community/:id`, and
    // `messages/new` before `messages/:id`. `community/admin` and
    // `community/moderation` are deliberately NOT behind adminGuard: area
    // admins (`manage_group`) and channel moderators are not `user_type`
    // admins. The backend answers 403 to everyone else and the page shows
    // "not permitted".
    return [
      { path: 'community', component: CommunityListPageComponent, canActivate: guards, data: { title: 'Community', animation: 'community' } },
      { path: 'community/c/:channelId', component: CommunityListPageComponent, canActivate: guards, data: { title: 'Community', animation: 'community' } },
      // Lands in a GROUP by key (first channel the viewer may see): what a dashboard
      // card or a role-driven menu entry links to, since channel ids are data.
      { path: 'community/g/:groupKey', component: CommunityListPageComponent, canActivate: guards, data: { title: 'Community', animation: 'community' } },
      { path: 'community/admin', component: CommunityAdminPageComponent, canActivate: guards, data: { title: 'Community Admin', animation: 'community-admin' } },
      { path: 'community/moderation', component: CommunityModerationPageComponent, canActivate: guards, data: { title: 'Community Moderation', animation: 'community-moderation' } },
      { path: 'community/messages', component: CommunityMessagesPageComponent, canActivate: guards, data: { title: 'Messages', animation: 'community-messages' } },
      { path: 'community/messages/new', component: CommunityMessageComposePageComponent, canActivate: guards, data: { title: 'New Message', animation: 'community-messages' } },
      { path: 'community/messages/:id', component: CommunityMessageDetailPageComponent, canActivate: guards, data: { title: 'Message', animation: 'community-messages' } },
      { path: 'community/notifications', component: CommunityNotificationsPageComponent, canActivate: guards, data: { title: 'Notifications', animation: 'community-notifications' } },
      { path: 'community/settings', component: CommunitySettingsPageComponent, canActivate: guards, data: { title: 'Notification Settings', animation: 'community-settings' } },
      { path: 'community/search', component: CommunitySearchPageComponent, canActivate: guards, data: { title: 'Community Search', animation: 'community-search' } },
      { path: 'community/u/:userId', component: CommunityMemberPageComponent, canActivate: guards, data: { title: 'Community Member', animation: 'community-member' } },
      { path: 'community/:id', component: CommunityThreadDetailPageComponent, canActivate: guards, data: { title: 'Community Thread', animation: 'community-thread' } },
    ];
  }

  tap(callback: (builder: this) => void): this {
    callback(this);
    return this;
  }

  private route(defaultRoute: Route): Route[] {
    if (this.removed.has(defaultRoute.path!)) return [];
    const r = this.replacements.get(defaultRoute.path!);
    return r ? (Array.isArray(r) ? r : [r]) : [defaultRoute];
  }

  private mediaRoutes(): Routes {
    if (this.removed.has('media')) return [];
    const r = this.replacements.get('media');
    if (r) return Array.isArray(r) ? r : [r];

    return [
      {
        path: 'media',
        children: [
          { matcher: mediaMatcher, component: MediaPageComponent,  data: { title: 'media', area: 'media', animation:'media' } },
          { matcher: infoMatcher, component: MediaPageComponent,  data: { title: 'info', area: 'info', animation:'info' } },
          { matcher: policyMatcher, component: MediaPageComponent,  data: { title: 'policy', area: 'policy', animation:'policy' } }
        ]
      },
    ];
  }

  build(): Routes {
    const main = this.mainComponent;

    // When maintenance is enabled, also guard the top-level routes (login, register, etc.)
    // so they cannot be reached while the backend feature flag is on.
    // Skip redirect routes — Angular forbids combining redirectTo with canActivate.
    const guardTop = (route: Route): Route =>
      this.maintenanceEnabled && !route.redirectTo
        ? { ...route, canActivate: [...(route.canActivate ?? []), maintenanceGuard] }
        : route;

    return [
      ...this.route({ path: 'login',
                      component: LoginPageComponent,
                      providers: [
                        {
                          provide: LOGIN_CONFIG,
                          useValue: {
                            register: true,
                            ...(this.registerUrl ? { registerUrl: this.registerUrl } : {}),
                            passwordRecovery: true,
                            appInstallBottomsheet: false,
                            ...(this.loginActions ? { actions: this.loginActions } : {})
                          } satisfies LoginConfig
                        }
                      ],
                      data: { animation: 'login' } }).map(guardTop),
      ...this.route({ path: 'login/:token', component: LoginPageComponent, data: { animation: 'login' } }).map(guardTop),
      ...this.route({ path: 'register', component: RegisterPageComponent, data: { animation: 'register' } }).map(guardTop),
      ...this.route({ path: 'confirm-email/:token', component: ConfirmEmailPageComponent, data: { animation: 'confirm-email' } }).map(guardTop),
      ...this.route({ path: 'forgot-password', component: ForgotPasswordPageComponent, data: { animation: 'forgot-password' } }).map(guardTop),
      ...this.route({ path: 'reset-password/:token', component: ResetPasswordPageComponent, data: { animation: 'reset-password' } }).map(guardTop),

      { path: 'sso/:token', redirectTo: 'login/:token' },
      // Cross-site SSO landing: target site exchanges the single-use handoff
      // token for a session (lang-agnostic, public). Available on every app.
      { path: 'auth/handoff/:token', component: CrossLoginHandoffPageComponent, data: { animation: 'handoff' } },
      ...this.config.root.map(guardTop),

      {
        path: ':lang',
        canActivate: this.maintenanceEnabled ? [LanguageGuard, maintenanceGuard] : [LanguageGuard],
        ...(this.maintenanceEnabled ? { canActivateChild: [maintenanceGuard] } : {}),
        children: [
          ...(this.maintenanceEnabled && this.maintenanceComponent
            ? [{
                path: 'maintenance',
                component: this.maintenanceComponent,
                canActivate: [noMaintenanceGuard],
                data: { animation: 'maintenance' }
              } as Route]
            : []),
          ...this.route({ path: 'confirm-email-and-setup/:token', component: ConfirmEmailSetupPageComponent, data: { animation: 'confirm-email-and-setup' } }),
          ...this.config.publicLang,
          {
            path: '',
            canActivate: [authGuard],
            children: [
              // Just uncomment to enable tos consent page - RouteService will perform the required navigation
              // ...this.route({ path: 'tos', component: TosPageComponent, data: { title: 'tos', animation: 'tos' } }),
              ...this.route({ path: 'profile/complete', component: ProfilePageComponent, data: { title: 'Profile', animation: 'profile' } }),
              // Sibling of the layout, not a child: the gate must not sit behind the
              // guard that redirects to it, or activating it would loop.
              ...(this.legalAcknowledgementEnabled
                ? [{
                    path: 'legal/acknowledge',
                    component: LegalAcknowledgementPageComponent,
                    data: { title: 'Legal Acknowledgement', animation: 'legal-acknowledge' }
                  } as Route]
                : []),
              ...this.config.authenticated,
              {
                path: '',
                component: DefaultLayoutComponent,
                // Order matters: verify the email first, then the acknowledgement.
                // Both bounce out of the layout, and asking someone to accept terms
                // before they have confirmed they own the address would be backwards.
                canActivate: this.legalAcknowledgementEnabled
                  ? [emailVerifiedGuard, legalAcknowledgementGuard]
                  : [emailVerifiedGuard],
                data: {
                  emailUnverifiedAllowlist:
                    this.emailUnverifiedAllowlist.length > 0 ? this.emailUnverifiedAllowlist : undefined
                },
                children: [
                  ...this.route({ path: 'dashboard', component: main, data: { title: 'Dashboard', animation: 'main' } }),
                  ...this.route({ path: 'main', component: main, data: { title: 'Dashboard', animation: 'main' } }),
                  ...this.route({ path: 'profile', component: ProfilePageComponent, data: { title: 'Profile', animation: 'profile' } }),
                  ...this.route({ path: 'my-orders', component: UserOrdersPageComponent, data: { title: 'My Orders', animation: 'my-orders' } }),
                  ...this.route({ path: 'my-orders/:id', component: OrderDetailPageComponent, data: { title: 'My Orders', animation: 'my-orders' } }),
                  ...this.route({ path: 'order-wizard', component: OrderWizardComponent, data: { title: 'Order Products', animation: 'order-wizard' } }),
                  ...this.mediaRoutes(),
                  ...this.config.protected,
                  ...this.communityRoutes(),
                  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
                  { path: '**', redirectTo: 'dashboard' },
                ],
              },
            ],
          },
        ],
      },

      { path: '', redirectTo: '/en', pathMatch: 'full' },
      { path: '**', redirectTo: '/en' },
    ];
  }
}

export const routeBuilder = () => new RouteBuilder();
